// legacy-bridge.js — il debito di convivenza con workspace-host/admin-module in UN file
// (design §1). Muore con R8 (ritiro workspace-host + migrazione chiavi legacy).
//
// Contiene:
//  - WSH CSS VERBATIM (workspace-host.js:34-99): 2 anni di edge case mobile/boot, si riusa
//    byte-per-byte — la parità è ASSERITA dal test shell-wsh-guard.test.js che esegue il
//    vero workspace-host.js in un DOM finto e confronta il textContent iniettato.
//  - ensureRootRule(id): regola bulletproof di esclusività root (workspace-host.js:205-211).
//  - proxyProc()/proxyAmm(): click programmatico sui bottoni .amm-rail-btn[data-ws] del rail
//    di admin-module (pattern già in produzione: workspace-host.js:173-177).
//  - routeStore: persistenza route con DOPPIA SCRITTURA (§7.3) shell_route + chiavi legacy
//    (wsh_active / amm_workspace) fino a R8, così i tab su build vecchia non perdono il restore.
//  - getUser(): l'utente autenticato del monolite (window._authedUser, index.html:9431).
//  - hideAmmRail(): DORMIENTE in R1 (serve da R2, quando il Rail React sostituisce #amm-rail).

export const RAIL_W = 56;

// Moduli "host" legacy: la loro attivazione passa dal rail di admin-module
// (click programmatico), NON dalle classi wsh-*. Hardcode consapevole del debito
// di convivenza: muore con R8.
export const HOST_IDS = ['proc', 'amm'];

// ---------------------------------------------------------------------------
// CSS wsh-* VERBATIM da workspace-host.js:34-99 (stesso array, stesso join('\n')).
// NON MODIFICARE: la parità byte-per-byte è verificata dal contract test B2.4r.
// ---------------------------------------------------------------------------
export function wshCss() {
  return [
    // Disattiva il FONT-BOOSTING di Chrome mobile (text autosizing): senza questo Chrome ingrandisce
    // automaticamente il testo dei blocchi larghi → tutto "troppo zoomato", mezza card per schermo.
    'html { -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }',
    '#cont-root, #cont-root * { -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }',
    // quando un workspace extra è attivo, nascondi Procurement + Amministrazione
    'body.wsh-active #sidebar,',
    'body.wsh-active > main,',
    'body.wsh-active #amm-sidebar,',
    'body.wsh-active #amm-main { display:none !important; }',
    // MOBILE: nascondi anche la chrome MOBILE di Procurement (top-bar <header> con ≡ #nav-toggle + #nav-drawer)
    // quando un workspace extra è attivo — altrimenti compare un 3° hamburger sovrapposto alla top-bar del modulo.
    'body.wsh-active > header,',
    'body.wsh-active header.md\\:hidden,',
    'body.wsh-active #nav-toggle,',
    'body.wsh-active #nav-drawer { display:none !important; }',
    // I FAB flottanti di Procurement (creati da voltora-enhancements.js: "+ Fornitore quick-add" e
    // "Quick Quote") sono fixed e restano su mobile a prescindere dal modulo → vanno nascosti quando è
    // attivo un workspace extra (Contabilità) o Amministrazione, altrimenti filtrano sopra il modulo.
    'body.wsh-active #voltora-mobile-fab,',
    'body.wsh-active #voltora-quote-fab,',
    'body.workspace-amm #voltora-mobile-fab,',
    'body.workspace-amm #voltora-quote-fab { display:none !important; }',
    // Quando un workspace extra (es. Cont) è attivo, i bottoni host Proc/Amm NON devono MAI
    // apparire "accesi" (anche se admin-module riassegna .active): neutralizza l'evidenziazione.
    'body.wsh-active #amm-rail .amm-rail-btn[data-ws].active { background:transparent !important; color:#94a3b8 !important; box-shadow:none !important; border-color:transparent !important; }',
    // lo spazio a sinistra resta solo per il rail (56px)
    '@media (min-width:768px){ body.wsh-active { padding-left:' + RAIL_W + 'px !important; } }',
    // la root del modulo Contabilità sta a destra del rail e riempie la pagina
    'body.wsh-active #cont-root { margin-left:0; }',
    // allinea la palette del modulo Contabilità al cyan della suite (override CSS variables)
    '#cont-root { --accent:#06b6d4 !important; --accent-dim:#cffafe !important; }',
    // BOOT: finché ripristiniamo il workspace attivo, nascondi rail + contenuti così i selettori
    // non "ballano" (proc→amm→cont). Si rivela lo stato finale tutto insieme.
    'body.wsh-booting #amm-rail,',
    'body.wsh-booting #sidebar,',
    'body.wsh-booting > main,',
    'body.wsh-booting #amm-sidebar,',
    'body.wsh-booting #amm-main,',
    'body.wsh-booting #cont-root { visibility:hidden !important; }',
    // I dropdown combo (es. filtro Fornitore in Fatture) devono stare SOPRA l'header sticky della tabella.
    '#cont-root .combo-dropdown { z-index:9999 !important; }',
    '#cont-root thead, #cont-root thead th { z-index:5; }',
    // MOBILE: il rail dei moduli è sostituito da una BARRA MODULO in basso (#wsh-modbar) che apre
    // uno sheet con l'elenco moduli (#wsh-modsheet). L'hamburger di ogni modulo resta il menu sezioni.
    '#wsh-modbar { display:none; }',
    '@media (max-width:767px){',
    '  #amm-rail { display:none !important; }',
    // niente più hamburger sparsi: la navigazione è tutta nella barra in basso
    '  #nav-toggle { display:none !important; }',
    '  #cont-root .mobile-topbar .hb { display:none !important; }',
    '  #wsh-modbar { display:flex; position:fixed; left:0; right:0; bottom:0; height:54px; z-index:600; background:#0b1220; color:#fff; }',
    '  #wsh-modbar .mb-half { flex:1; min-width:0; display:flex; align-items:center; justify-content:center; gap:7px; background:transparent; border:none; color:#fff; font-weight:700; font-size:13px; letter-spacing:0.3px; cursor:pointer; height:100%; padding:0 8px; }',
    '  #wsh-modbar .mb-half span { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }',
    '  #wsh-modbar .mb-right { border-left:1px solid rgba(255,255,255,0.16); }',
    '  #wsh-modbar svg { width:20px; height:20px; flex-shrink:0; }',
    '  #wsh-modsheet-bg { position:fixed; inset:0; z-index:1200; background:rgba(0,0,0,0.5); display:none; align-items:flex-end; }',
    '  #wsh-modsheet-bg.open { display:flex; }',
    '  #wsh-modsheet { background:#fff; width:100%; border-radius:16px 16px 0 0; padding:8px 0 14px; max-height:80vh; overflow:auto; }',
    '  #wsh-modsheet h4 { margin:14px 18px 6px; font-size:12px; text-transform:uppercase; letter-spacing:1px; color:#8a93a6; }',
    '  .wsh-mod { display:flex; align-items:center; gap:12px; padding:14px 18px; font-size:16px; color:#1a1f2e; cursor:pointer; }',
    '  .wsh-mod .mi { width:34px; height:34px; border-radius:8px; background:#0b1220; color:#fff; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; flex-shrink:0; }',
    '  .wsh-mod.active { color:#0891b2; font-weight:700; }',
    '  .wsh-mod.active .mi { background:#06b6d4; }',
    '}',
  ].join('\n');
}

// injectStyle di wsh (workspace-host.js:30-34, senza la coda ensureModbar: la barra è React).
export function injectWshStyle(doc) {
  if (doc.getElementById('wsh-style')) return;
  const css = doc.createElement('style');
  css.id = 'wsh-style';
  css.textContent = wshCss();
  doc.head.appendChild(css);
}

// Regola CSS BULLETPROOF per ogni workspace (workspace-host.js:205-211, VERBATIM):
// la root è display:none di default e diventa visibile SOLO con body.wsh-<id>.
export function ensureRootRule(doc, id) {
  const sid = 'wsh-root-rule-' + id;
  if (doc.getElementById(sid)) return;
  const st = doc.createElement('style');
  st.id = sid;
  st.textContent = '#' + id + '-root{display:none !important;} body.wsh-' + id + ' #' + id + '-root{display:block !important;}';
  doc.head.appendChild(st);
}

export function railEl(doc) {
  return doc.getElementById('amm-rail');
}

// Click programmatico su un bottone host del rail admin (pattern wsh:173-177).
// → true se il bottone esiste ed è stato cliccato.
export function proxyRail(doc, ws) {
  const rail = railEl(doc);
  const b = rail && rail.querySelector('.amm-rail-btn[data-ws="' + ws + '"]');
  if (b) { b.click(); return true; }
  return false;
}

export function proxyProc(doc) { return proxyRail(doc, 'proc'); }
export function proxyAmm(doc) { return proxyRail(doc, 'amm'); }

// Utente autenticato del monolite (API mode): window._authedUser — index.html:9431.
export function getUser(win) {
  return (win && win._authedUser) || null;
}

// DORMIENTE in R1 (serve da R2/R3): nasconde il rail legacy quando il Rail React
// è attivo. Flag reversibile: rimuovere la classe fa ricomparire tutto.
export function hideAmmRail(doc, hide) {
  if (!doc.body) return;
  doc.body.classList.toggle('shell-rail-v2', !!hide);
}

// ---------------------------------------------------------------------------
// routeStore — persistenza route con doppia scrittura (§7.3), fino a R8.
//  write({module,item?,params?}):
//    - shell_route = JSON della route (chiave NUOVA)
//    - wsh_active  = <id> se il modulo è un ospite wsh (non-host), RIMOSSA altrimenti
//      (parità con activate/deactivate di workspace-host)
//    - amm_workspace = 'amm' SOLO quando il modulo attivo è amm (il valore 'proc'
//      lo scrive già admin-module sul click del rail — non lo dupplichiamo)
//  read(): shell_route parsata o null.
//  restoreTarget(): l'ospite wsh da ripristinare al boot —
//    shell_route (se presente decide lei: ospite → id, host → null)
//    || wsh_active legacy (sessioni pre-shell / tab su build vecchia).
// ---------------------------------------------------------------------------
export function createRouteStore(win, isGuestId) {
  function write(route) {
    if (!route || typeof route.module !== 'string') return;
    try { win.localStorage.setItem('shell_route', JSON.stringify(route)); } catch (e) { /* storage pieno/negato: le chiavi legacy sotto restano la verità */ }
    try {
      if (isGuestId(route.module)) win.localStorage.setItem('wsh_active', route.module);
      else win.localStorage.removeItem('wsh_active');
      if (route.module === 'amm') win.localStorage.setItem('amm_workspace', 'amm');
    } catch (e) { /* come sopra */ }
  }

  function read() {
    try {
      const s = win.localStorage.getItem('shell_route');
      if (!s) return null;
      const r = JSON.parse(s);
      if (r && typeof r.module === 'string') return r;
    } catch (e) { /* JSON corrotto = come assente */ }
    return null;
  }

  function readLegacyActive() {
    try { return win.localStorage.getItem('wsh_active'); } catch (e) { return null; }
  }

  function restoreTarget() {
    const r = read();
    if (r) return isGuestId(r.module) ? r.module : null;
    return readLegacyActive();
  }

  return { write, read, readLegacyActive, restoreTarget };
}
